export const color = {
  bluePrimary: '#5C7BEB' as const,
  blueDarker: '#3D529C' as const,
  blueDark: '#5772D3' as const,
  blueLight: '#92A7F1' as const,
  blueLightMedium: '#D4DEFF' as const,
  blueLighter: '#EBEFFD' as const,

  green: '#4ABA98' as const,
  greenDark: '#42A98A' as const,
  greenDarker: '#318168' as const,
  greenLight: '#DDFAEC' as const,
  greenLighter: '#55DEB4' as const,
  emeraldLight: '#ECFFF6' as const,

  yellow: '#FFDA46' as const,
  yellowChart: '#ffd25e' as const,
  yellowLight: '#FFF8DC' as const,
  yellowDark: '#E1840F' as const,

  red: '#F67574' as const,
  redDark: '#FFCCD0' as const,
  redLight: '#FFEBEC' as const,
  redChart: '#e55f5f' as const,

  /** Benchmark comparison lines — keep hex in sync with `--chart-benchmark-line-*` in themes (zoe.css). */
  chartBenchmarkLine0: '#eb6a6a' as const,
  chartBenchmarkLine1: '#f59e0b' as const,
  chartBenchmarkLine2: '#5373e3' as const,
  chartBenchmarkLine3: '#9b51e0' as const,

  // Monochrome

  lighter: '#FCFCFC' as const,
  light: '#F7F7F7' as const,
  mediumLight: '#DCDCDC' as const,
  medium: '#B4B4B4' as const,
  mediumDark: '#8B8B8B' as const,
  dark: '#5F5F5F' as const,
  darker: '#3F3E3A' as const,

  secondaryDark: '#6F7485' as const,
  secondary: '#747D9B' as const,
  secondaryLight: '#DADEE9' as const,
};

export const filter = {
  /** ### Is #5C7BEB */
  bluePrimary:
    'invert(53%) sepia(45%) saturate(6186%) hue-rotate(212deg) brightness(99%) contrast(86%)' as const,
  /** ### Is #3D529C */
  blueDarker:
    'invert(31%) sepia(12%) saturate(3878%) hue-rotate(193deg) brightness(92%) contrast(84%)' as const,
  /** ### Is #5772D3 */
  blueDark:
    'invert(45%) sepia(83%) saturate(2503%) hue-rotate(211deg) brightness(89%) contrast(84%)' as const,
  /** ### Is #92A7F1 */
  blueLight:
    'invert(76%) sepia(14%) saturate(4480%) hue-rotate(191deg) brightness(95%) contrast(99%)' as const,
  /** ### Is #EBEFFD */
  blueLighter:
    'invert(87%) sepia(31%) saturate(93%) hue-rotate(192deg) brightness(102%) contrast(98%)' as const,

  green:
    'invert(65%) sepia(48%) saturate(438%) hue-rotate(111deg) brightness(89%) contrast(90%)' as const,
  greenLight:
    'invert(95%) sepia(7%) saturate(849%) hue-rotate(74deg) brightness(108%) contrast(96%)' as const,
  /** ### Is #4ABA98 */
  emerald:
    'brightness(0) saturate(100%) invert(64%) sepia(64%) saturate(373%) hue-rotate(111deg) brightness(91%) contrast(79%)' as const,

  yellow:
    'invert(89%) sepia(24%) saturate(1269%) hue-rotate(341deg) brightness(102%) contrast(100%)' as const,
  yellowLight:
    'invert(88%) sepia(11%) saturate(704%) hue-rotate(333deg) brightness(109%) contrast(107%)' as const,
  /** ### Is #FFB75D */
  yellowDark:
    'brightness(0) saturate(100%) invert(79%) sepia(43%) saturate(756%) hue-rotate(326deg) brightness(100%) contrast(101%)' as const,

  red: 'invert(79%) sepia(51%) saturate(6216%) hue-rotate(318deg) brightness(106%) contrast(92%)' as const,
  redLight:
    'invert(85%) sepia(13%) saturate(374%) hue-rotate(309deg) brightness(109%) contrast(101%)' as const,

  // Monochrome

  lighter:
    'invert(100%) sepia(100%) saturate(0%) hue-rotate(165deg) brightness(108%) contrast(98%);' as const,
  light:
    'invert(100%) sepia(72%) saturate(52%) hue-rotate(16deg) brightness(108%) contrast(94%)' as const,
  mediumLight:
    'invert(100%) sepia(23%) saturate(6681%) hue-rotate(274deg) brightness(160%) contrast(73%)' as const,
  medium:
    'invert(81%) sepia(15%) saturate(0%) hue-rotate(212deg) brightness(85%) contrast(97%)' as const,
  mediumDark:
    'invert(58%) sepia(0%) saturate(1323%) hue-rotate(184deg) brightness(95%) contrast(80%)' as const,
  dark: 'invert(36%) sepia(24%) saturate(0%) hue-rotate(207deg) brightness(96%) contrast(91%)' as const,
  darker:
    'invert(21%) sepia(2%) saturate(1498%) hue-rotate(11deg) brightness(103%) contrast(89%)' as const,

  secondaryDark:
    'invert(47%) sepia(13%) saturate(429%) hue-rotate(189deg) brightness(95%) contrast(93%)' as const,
  secondary:
    'invert(54%) sepia(14%) saturate(681%) hue-rotate(189deg) brightness(88%) contrast(86%)' as const,
  secondaryLight:
    'invert(94%) sepia(4%) saturate(625%) hue-rotate(191deg) brightness(96%) contrast(91%)' as const,
  /** ### Is #747D9B */
  secondaryVariation:
    'invert(49%) sepia(24%) saturate(381%) hue-rotate(189deg) brightness(95%) contrast(92%);' as const,
};

export const typography = {
  type: {
    primary: `var(--family-paragraph)` as const,
    titles: `var(--family-title)` as const,
  },
  weight: {
    regular: '400' as const,
    medium: '500' as const,
    bold: '700' as const,
    black: '900' as const,
  },
  size: {
    s1: '12px' as const,
    s2: '14px' as const,
    s3: '16px' as const,
    s4: '18px' as const,
    m1: '20px' as const,
    m2: '24px' as const,
    m3: '30px' as const,
    l1: '32px' as const,
    l2: '56px' as const,
  },
};

export const spacing = {
  padding: {
    small: '24px' as const,
    large: '32px' as const,
  },
  borderRadius: {
    small: '8px' as const,
    medium: '12px' as const,
    large: '16px' as const,
  },
};

export const breakpoint = {
  mobile: '768px' as const,
  mobileMax: '767px' as const,
};
